import React, { useState } from "react";
import { motion } from "framer-motion";
import { Upload, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MEDIA } from "@/lib/media";

const PROJECT_TYPES = ["Multiline Railings", "Panel Railings", "Picket Railings", "Cable Railings", "Handrail Only", "Other"];
const BUDGETS = ["Under $100k", "$100k – $500k", "$500k – $1M", "$1M – $5M", "$5M+"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] tracking-[0.15em] uppercase text-onyx-metal">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls = "w-full bg-transparent border-b border-black/20 py-3 text-onyx-charcoal focus:border-onyx-black outline-none transition-colors placeholder:text-onyx-metal/50";

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", project_location: "", project_type: "", budget: "", description: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, { name: file.name, path: res.data.path }]);
      }
      toast.success("Files uploaded");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, files: files.map((f) => f.path) });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="grid lg:grid-cols-2 min-h-screen">
        <div className="relative hidden lg:block">
          <img src={MEDIA.glassStair} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute bottom-16 left-12 right-12">
            <span className="text-onyx-wood text-[11px] tracking-[0.35em] uppercase">Contact</span>
            <h2 className="text-white font-display font-extrabold text-5xl xl:text-6xl tracking-tightest mt-5 leading-none">Let's build something exceptional.</h2>
          </div>
        </div>

        <div className="px-6 lg:px-16 pt-32 lg:pt-40 pb-24 flex items-center">
          <div className="w-full max-w-xl mx-auto">
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-20" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-onyx-black text-white flex items-center justify-center mx-auto mb-8"><Check size={28} /></div>
                <h3 className="font-display font-extrabold text-3xl tracking-tightest">Thank you.</h3>
                <p className="text-onyx-metal mt-4 text-lg">Our team will review your project and contact you shortly.</p>
              </motion.div>
            ) : (
              <>
                <h1 className="lg:hidden font-display font-extrabold text-4xl tracking-tightest mb-3">Let's build something exceptional.</h1>
                <p className="text-onyx-metal mb-10">Tell us about your project. Attach plans, drawings, or reference images.</p>
                <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-7">
                    <Field label="Name *"><input data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" /></Field>
                    <Field label="Company"><input data-testid="contact-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company" /></Field>
                    <Field label="Email *"><input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" /></Field>
                    <Field label="Phone"><input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" /></Field>
                    <Field label="Project Location"><input data-testid="contact-location" className={inputCls} value={form.project_location} onChange={set("project_location")} placeholder="City, State" /></Field>
                    <Field label="Project Type">
                      <select data-testid="contact-type" className={`${inputCls} appearance-none`} value={form.project_type} onChange={set("project_type")}>
                        <option value="">Select…</option>
                        {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Estimated Budget">
                    <select data-testid="contact-budget" className={`${inputCls} appearance-none`} value={form.budget} onChange={set("budget")}>
                      <option value="">Select…</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                  <Field label="Description">
                    <textarea data-testid="contact-description" rows={4} className={`${inputCls} resize-none`} value={form.description} onChange={set("description")} placeholder="Scope, timeline, and any details…" />
                  </Field>

                  <div>
                    <span className="text-[11px] tracking-[0.15em] uppercase text-onyx-metal">Plans / Drawings / Images</span>
                    <label className="mt-3 border border-dashed border-black/25 hover:border-onyx-black transition-colors flex items-center justify-center gap-3 py-8 cursor-pointer" data-testid="contact-upload">
                      {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                      <span className="text-sm text-onyx-metal">{uploading ? "Uploading…" : "Click to upload files"}</span>
                      <input type="file" multiple className="hidden" onChange={onFiles} />
                    </label>
                    {files.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {files.map((f, i) => (
                          <div key={i} className="flex items-center justify-between text-sm bg-black/5 px-3 py-2">
                            <span className="truncate">{f.name}</span>
                            <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))}><X size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button data-testid="contact-submit" disabled={submitting} className="w-full bg-onyx-black text-white py-4 text-[12px] tracking-[0.15em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="animate-spin" size={16} />} Submit Project Inquiry
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
